import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@kizen/kds/Icon';
import { PiChatToolRow, type ToolItem } from 'ts-components/PiChat/PiChatLog';
import { parsePushSummary } from './pushSummary';
import { resolveCaptureUrl } from './captureUrl';
import type { RestoreEvent } from './restoreEvents';
import { CaptureThumb, EventRow, ToolWithCapture } from './styles';

interface CaptureImage {
  url: string;
  width: number;
  height: number;
}

const CaptureThumbnail: FC<{ image: CaptureImage }> = ({ image }) => {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <CaptureThumb
      href={image.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('Open the block screenshot in a new tab')}
      data-qa-id="ai-block-panel-capture-image"
    >
      <img
        src={image.url}
        alt=""
        width={image.width}
        height={image.height}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </CaptureThumb>
  );
};

const rowCaptureImage = (
  item: ToolItem,
  wsUrl: string
): CaptureImage | undefined => {
  if (item.status !== 'ok' || !item.capture) return undefined;
  const url = resolveCaptureUrl(item.capture.path, wsUrl);
  return url
    ? { url, width: item.capture.width, height: item.capture.height }
    : undefined;
};

const PushRow: FC<{ action: 'created' | 'updated' }> = ({ action }) => {
  const { t } = useTranslation();
  return (
    <EventRow $tone="success" data-qa-id="ai-block-panel-push-result">
      <Icon icon="custom-circle-check" size="sm" color="feedback-success" />
      {action === 'created' ? t('Block created') : t('Block updated')}
    </EventRow>
  );
};

export const BlockToolRow: FC<{
  item: ToolItem;
  wsUrl: string;
}> = ({ item, wsUrl }) => {
  const push = parsePushSummary(item);
  const image = rowCaptureImage(item, wsUrl);
  return (
    <>
      {image ? (
        <ToolWithCapture>
          <PiChatToolRow item={item} />
          <CaptureThumbnail key={image.url} image={image} />
        </ToolWithCapture>
      ) : (
        <PiChatToolRow item={item} />
      )}
      {push ? <PushRow action={push.action} /> : null}
    </>
  );
};

export const RestoreEventRow: FC<{ event: RestoreEvent }> = ({ event }) => {
  const { t } = useTranslation();
  return (
    <EventRow $tone="info" data-qa-id="ai-block-panel-restore-event">
      <Icon
        icon={event.direction === 'undo' ? 'text-undo' : 'text-redo'}
        size="sm"
        color="font-secondary"
      />
      {t('Restored version {{version}}', { version: event.version })}
    </EventRow>
  );
};
